/**
 * Owner: Masa Bayu
 * File: publisher-dashboard.ts
 * Date:Time: 2026-10-23 07:25
 * Description: §467-C — klien + stor dashboard penerbit qxread. Sumber data
 *   HANYA `GET /v1/publisher/dashboard` qxp-hub dengan Bearer sesi hub (§467-B).
 *   Tiada nilai lalai: respons yang tidak boleh dihurai = status `error`,
 *   BUKAN dashboard kosong (doktrin gagal-keras §457 F0 — katalog separa
 *   dahulu terbaca sebagai "tiada buku").
 *
 *   Status: `anonymous` (tiada token) · `loading` · `ready` · `forbidden`
 *   (sesi sah tapi bukan penerbit — 403) · `error`.
 */

import { useSyncExternalStore } from 'react';
import { clearHubAuth, getHubAuthToken, setHubAuthRole } from '../../publication/hub-auth';
import { expireHubSession, qxpHubOrigin } from '../../publication/hub-sync';

export interface HubMoney {
  readonly currency: string;
  readonly minorUnits: number;
  readonly orders: number;
}

export interface HubMonthPoint {
  readonly month: string;
  readonly orders: number;
  readonly revenueByCurrency: readonly HubMoney[];
}

export interface HubBookSales {
  readonly orders: number;
  readonly buyers: number;
  readonly revenueByCurrency: readonly HubMoney[];
}

export interface HubRevision {
  readonly revision: number;
  readonly publishedAt: string | null;
}

export interface HubPublisherBook {
  readonly publicationId: string;
  readonly title: string;
  readonly languageRef: string | null;
  readonly revision: number | null;
  readonly publishedAt: string | null;
  readonly priceMinorUnits: number | null;
  readonly currency: string | null;
  readonly sampleMaxPage: number | null;
  readonly category: string | null;
  readonly subcategory: string | null;
  readonly artifactBaseUrl: string | null;
  readonly history: readonly HubRevision[];
  readonly sales: HubBookSales;
}

export interface HubOrderRow {
  readonly orderId: string;
  readonly publicationId: string;
  readonly title: string;
  readonly amountMinorUnits: number | null;
  readonly currency: string | null;
  readonly state: string | null;
  readonly createdAt: string | null;
  readonly paidAt: string | null;
  readonly buyerEmail: string | null;
}

export interface HubIdentityView {
  readonly accountId: string | null;
  readonly email: string | null;
  readonly role: string;
  readonly displayName: string;
  readonly published: readonly string[];
  readonly hasPassword: boolean;
  readonly googleLinked: boolean;
  readonly createdAt: string | null;
}

export interface PublisherDashboardView {
  readonly generatedAt: string;
  readonly identity: HubIdentityView;
  readonly totals: {
    readonly booksPublished: number;
    readonly paidOrders: number;
    readonly pendingOrders: number;
    readonly buyers: number;
    readonly revenueByCurrency: readonly HubMoney[];
  };
  readonly monthly: readonly HubMonthPoint[];
  readonly publications: readonly HubPublisherBook[];
  readonly recentOrders: readonly HubOrderRow[];
}

export type PublisherDashboardStatus = 'anonymous' | 'loading' | 'ready' | 'forbidden' | 'error';

export interface PublisherDashboardState {
  readonly status: PublisherDashboardStatus;
  readonly view: PublisherDashboardView | null;
  readonly error: string | null;
  readonly fetchedAt: string | null;
}

const IDLE: PublisherDashboardState = {
  status: 'anonymous',
  view: null,
  error: null,
  fetchedAt: null,
};

let state: PublisherDashboardState = IDLE;
let inflight: Promise<PublisherDashboardState> | null = null;
const listeners = new Set<() => void>();

function emit(next: PublisherDashboardState): void {
  state = next;
  for (const listener of listeners) listener();
}

export function getPublisherDashboard(): PublisherDashboardState {
  return state;
}

export function subscribePublisherDashboard(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function usePublisherDashboard(): PublisherDashboardState {
  return useSyncExternalStore(
    subscribePublisherDashboard,
    getPublisherDashboard,
    getPublisherDashboard,
  );
}

// ─── penghuraian (gagal-keras) ───────────────────────────────────────────────

type Record_ = Record<string, unknown>;

function isRecord(value: unknown): value is Record_ {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function str(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

function strOrNull(value: unknown): string | null {
  return typeof value === 'string' ? value : null;
}

function intOrNull(value: unknown): number | null {
  return typeof value === 'number' && Number.isInteger(value) ? value : null;
}

function int(value: unknown): number {
  const n = intOrNull(value);
  return n ?? 0;
}

function moneyList(value: unknown): readonly HubMoney[] {
  if (!Array.isArray(value)) return [];
  return value
    .filter(isRecord)
    .map((row) => ({
      currency: str(row.currency),
      minorUnits: int(row.minorUnits),
      orders: int(row.orders),
    }))
    .filter((row) => row.currency.length > 0);
}

function sales(value: unknown): HubBookSales {
  if (!isRecord(value)) return { orders: 0, buyers: 0, revenueByCurrency: [] };
  return {
    orders: int(value.orders),
    buyers: int(value.buyers),
    revenueByCurrency: moneyList(value.revenueByCurrency),
  };
}

function book(value: unknown): HubPublisherBook | null {
  if (!isRecord(value)) return null;
  const publicationId = str(value.publicationId);
  if (publicationId.length === 0) return null;
  return {
    publicationId,
    title: str(value.title) || publicationId,
    languageRef: strOrNull(value.languageRef),
    revision: intOrNull(value.revision),
    publishedAt: strOrNull(value.publishedAt),
    priceMinorUnits: intOrNull(value.priceMinorUnits),
    currency: strOrNull(value.currency),
    sampleMaxPage: intOrNull(value.sampleMaxPage),
    category: strOrNull(value.category),
    subcategory: strOrNull(value.subcategory),
    artifactBaseUrl: strOrNull(value.artifactBaseUrl),
    history: Array.isArray(value.history)
      ? value.history
          .filter(isRecord)
          .map((h) => ({ revision: int(h.revision), publishedAt: strOrNull(h.publishedAt) }))
      : [],
    sales: sales(value.sales),
  };
}

function orderRow(value: unknown): HubOrderRow | null {
  if (!isRecord(value)) return null;
  const orderId = str(value.orderId);
  if (orderId.length === 0) return null;
  return {
    orderId,
    publicationId: str(value.publicationId),
    title: str(value.title) || str(value.publicationId),
    amountMinorUnits: intOrNull(value.amountMinorUnits),
    currency: strOrNull(value.currency),
    state: strOrNull(value.state),
    createdAt: strOrNull(value.createdAt),
    paidAt: strOrNull(value.paidAt),
    buyerEmail: strOrNull(value.buyerEmail),
  };
}

/** null = bentuk respons bukan dashboard; pemanggil mesti paparkan ralat, bukan 0. */
export function parsePublisherDashboard(body: unknown): PublisherDashboardView | null {
  if (!isRecord(body) || body.ok !== true) return null;
  const identity = isRecord(body.identity) ? body.identity : null;
  const totals = isRecord(body.totals) ? body.totals : null;
  if (identity === null || totals === null) return null;
  const accountId = strOrNull(identity.accountId);
  const email = strOrNull(identity.email);
  if (accountId === null && email === null) return null;

  const publications = Array.isArray(body.publications)
    ? body.publications.map(book).filter((b): b is HubPublisherBook => b !== null)
    : null;
  const monthly = Array.isArray(body.monthly)
    ? body.monthly
        .filter(isRecord)
        .map((m) => ({
          month: str(m.month),
          orders: int(m.orders),
          revenueByCurrency: moneyList(m.revenueByCurrency),
        }))
        .filter((m) => m.month.length > 0)
    : null;
  const recentOrders = Array.isArray(body.recentOrders)
    ? body.recentOrders.map(orderRow).filter((o): o is HubOrderRow => o !== null)
    : null;
  if (publications === null || monthly === null || recentOrders === null) return null;

  return {
    generatedAt: str(body.generatedAt),
    identity: {
      accountId,
      email,
      role: str(identity.role),
      displayName: str(identity.displayName),
      published: Array.isArray(identity.published)
        ? identity.published.filter((id): id is string => typeof id === 'string')
        : [],
      hasPassword: identity.hasPassword === true,
      googleLinked: identity.googleLinked === true,
      createdAt: strOrNull(identity.createdAt),
    },
    totals: {
      booksPublished: int(totals.booksPublished),
      paidOrders: int(totals.paidOrders),
      pendingOrders: int(totals.pendingOrders),
      buyers: int(totals.buyers),
      revenueByCurrency: moneyList(totals.revenueByCurrency),
    },
    monthly,
    publications,
    recentOrders,
  };
}

// ─── rantaian ────────────────────────────────────────────────────────────────

export type DashboardFetchInit = {
  readonly method?: string;
  readonly headers?: Record<string, string>;
};
export type DashboardFetch = (url: string, init?: DashboardFetchInit) => Promise<Response>;

/** Mesej ralat hub = `{ok:false, error:'…'}`; selain itu mesej generik berjujur. */
async function hubErrorFor(res: Response): Promise<string> {
  try {
    const body = (await res.json()) as Record_;
    const message = str(body.error);
    if (message.length > 0) return message;
  } catch {
    /* badan bukan JSON */
  }
  return `Pelayan memulangkan HTTP ${String(res.status)}`;
}

export type OwnedReleaseResult =
  | { readonly ok: true }
  | { readonly ok: false; readonly error: string };

/** Buang terbitan milik sesi penerbit daripada katalog hub. */
export async function removeOwnedPublication(
  publicationId: string,
  options: { readonly fetchImpl?: DashboardFetch; readonly origin?: string } = {},
): Promise<OwnedReleaseResult> {
  const token = getHubAuthToken();
  if (token === null) return { ok: false, error: 'Sesi penerbit tiada' };
  const fetchImpl = options.fetchImpl ?? fetch;
  const origin = options.origin ?? qxpHubOrigin();
  let res: Response;
  try {
    res = await fetchImpl(`${origin}/v1/publications/${encodeURIComponent(publicationId)}`, {
      method: 'DELETE',
      headers: { authorization: `Bearer ${token}` },
    });
  } catch {
    return { ok: false, error: 'Pelayan identiti tidak dapat dicapai' };
  }
  if (!res.ok) {
    let message = `Pelayan memulangkan HTTP ${String(res.status)}`;
    try {
      const body = (await res.json()) as Record_;
      const error = str(body.error);
      if (error.length > 0) message = error;
    } catch {
      /* badan bukan JSON */
    }
    return { ok: false, error: message };
  }
  await refreshPublisherDashboard({ force: true, fetchImpl, origin });
  return { ok: true };
}

/**
 * Tarik dashboard penerbit. `force=false` (lalai) memakai cached `ready` untuk
 * render pertama supaya skrin tidak berkedip; token tiada → `anonymous`.
 */
export async function refreshPublisherDashboard(
  options: {
    readonly force?: boolean;
    readonly fetchImpl?: DashboardFetch;
    readonly origin?: string;
  } = {},
): Promise<PublisherDashboardState> {
  const token = getHubAuthToken();
  if (token === null) {
    emit(IDLE);
    return IDLE;
  }
  if (state.status === 'ready' && options.force !== true) return state;
  if (inflight !== null) return inflight;

  emit({ ...state, status: 'loading' });
  const fetchImpl = options.fetchImpl ?? fetch;
  const origin = options.origin ?? qxpHubOrigin();

  const task = (async (): Promise<PublisherDashboardState> => {
    let res: Response;
    try {
      res = await fetchImpl(`${origin}/v1/publisher/dashboard`, {
        method: 'GET',
        headers: { authorization: `Bearer ${token}` },
      });
    } catch {
      return fail('Pelayan identiti tidak dapat dicapai');
    } finally {
      inflight = null;
    }
    if (res.status === 401) {
      expireHubSession();
      emit(IDLE);
      return IDLE;
    }
    if (res.status === 403) {
      emit({ status: 'forbidden', view: null, error: await hubErrorFor(res), fetchedAt: null });
      return state;
    }
    if (!res.ok) {
      return fail(await hubErrorFor(res));
    }
    let body: unknown;
    try {
      body = await res.json();
    } catch {
      return fail('Respons dashboard bukan JSON');
    }
    const view = parsePublisherDashboard(body);
    if (view === null) return fail('Respons dashboard tidak dapat dihurai');
    setHubAuthRole(view.identity.role);
    emit({ status: 'ready', view, error: null, fetchedAt: view.generatedAt });
    return state;
  })();

  inflight = task;
  return task;
}

function fail(error: string): PublisherDashboardState {
  emit({ ...state, status: 'error', error });
  return state;
}

/** Buang cache setempat (log keluar / tukar akaun). */
export function resetPublisherDashboard(): void {
  inflight = null;
  emit(IDLE);
}

/** Pembantu ujian: log keluar penuh + stor dashboard kosong. */
export function __resetPublisherDashboardForTests(): void {
  clearHubAuth();
  resetPublisherDashboard();
}
